/**
 * Universal Results Viewer — Output Pipeline deliverables viewer
 * Displays executive brief, action items, charts, detailed analysis, and raw agent outputs
 * for any template (HAVEN, HARVEST, VISTA, Sales, custom)
 */
import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent } from "../components/ui/dialog";
import {
  BarChart3, Brain, CheckCircle, Clock, DollarSign, Download, Printer,
  ChevronDown, ChevronRight, AlertTriangle, Target, Layers, FileText,
  TrendingUp, Shield, Zap, Eye, ArrowUpRight, Filter,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

// ── Helpers ──────────────────────────────────────────────────────────

const PRIORITY_COLORS = {
  critical: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30" },
  high:     { bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/30" },
  medium:   { bg: "bg-yellow-500/15", text: "text-yellow-400", border: "border-yellow-500/30" },
  low:      { bg: "bg-blue-500/15", text: "text-blue-400", border: "border-blue-500/30" },
};

const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"];

function fmtCost(c) { return c != null ? `$${Number(c).toFixed(4)}` : "—"; }
function fmtDuration(ms) { return ms ? `${(ms / 1000).toFixed(1)}s` : "—"; }

function safeParseJSON(text) {
  if (!text) return null;
  try {
    const cleaned = text.replace(/```json\s*/gi, "").replace(/```\s*/gi, "").trim();
    return JSON.parse(cleaned);
  } catch {
    // Try to find JSON array in the text
    const match = text.match(/\[[\s\S]*\]/);
    if (match) try { return JSON.parse(match[0]); } catch { /* noop */ }
    const objMatch = text.match(/\{[\s\S]*\}/);
    if (objMatch) try { return JSON.parse(objMatch[0]); } catch { /* noop */ }
    return null;
  }
}

/* Markdown renderer with executive styling */
function ExecMarkdown({ content }) {
  if (!content) return null;
  return (
    <ReactMarkdown
      components={{
        h1: ({ children }) => <h1 className="text-xl font-bold text-[var(--qn-foreground)] mt-6 mb-3 border-b border-[var(--qn-border)] pb-2">{children}</h1>,
        h2: ({ children }) => <h2 className="text-lg font-semibold text-[var(--qn-foreground)] mt-5 mb-2">{children}</h2>,
        h3: ({ children }) => <h3 className="text-base font-semibold text-[var(--qn-foreground)] mt-4 mb-1">{children}</h3>,
        p: ({ children }) => <p className="text-sm leading-relaxed text-[var(--qn-foreground)]/90 mb-3">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold text-[var(--qn-foreground)]">{children}</strong>,
        ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-3 text-sm text-[var(--qn-foreground)]/85">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 mb-3 text-sm text-[var(--qn-foreground)]/85">{children}</ol>,
        li: ({ children }) => <li className="ml-2">{children}</li>,
        table: ({ children }) => <div className="overflow-x-auto mb-4"><table className="w-full text-xs border-collapse">{children}</table></div>,
        th: ({ children }) => <th className="border border-[var(--qn-border)] px-3 py-1.5 bg-[var(--qn-surface)] text-left font-semibold text-[var(--qn-foreground)]">{children}</th>,
        td: ({ children }) => <td className="border border-[var(--qn-border)] px-3 py-1.5 text-[var(--qn-foreground)]/80">{children}</td>,
        blockquote: ({ children }) => <blockquote className="border-l-3 border-[var(--qn-primary)] pl-4 italic text-[var(--qn-foreground)]/70 my-3">{children}</blockquote>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}


// ── Chart Renderer ───────────────────────────────────────────────────

function ChartRenderer({ spec }) {
  if (!spec || !spec.data) return null;
  const { type, title, data, xKey = "name", yKey = "value", insight } = spec;

  return (
    <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid={`chart-${title?.replace(/\s/g, "-")}`}>
      <CardHeader className="py-2 px-4">
        <CardTitle className="text-xs font-semibold text-[var(--qn-foreground)]">{title}</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            {type === "pie" ? (
              <PieChart>
                <Pie data={data} dataKey={yKey} nameKey={xKey} cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11 }} />
              </PieChart>
            ) : type === "line" ? (
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} />
                <YAxis tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} />
                <Tooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11 }} />
                <Line type="monotone" dataKey={yKey} stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            ) : type === "area" ? (
              <AreaChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} />
                <YAxis tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} />
                <Tooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11 }} />
                <Area type="monotone" dataKey={yKey} stroke="#10b981" fill="#10b981" fillOpacity={0.15} />
              </AreaChart>
            ) : type === "radar" ? (
              <RadarChart cx="50%" cy="50%" outerRadius={70} data={data}>
                <PolarGrid stroke="rgba(148,163,184,0.15)" />
                <PolarAngleAxis dataKey={xKey} tick={{ fontSize: 9, fill: "var(--qn-foreground-muted)" }} />
                <Radar dataKey={yKey} stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} />
              </RadarChart>
            ) : (
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} />
                <YAxis tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} />
                <Tooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11 }} />
                <Bar dataKey={yKey} radius={[4, 4, 0, 0]}>
                  {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
        {insight && <p className="text-[10px] text-[var(--qn-foreground-muted)] mt-2 italic">{insight}</p>}
      </CardContent>
    </Card>
  );
}


// ── Action Items Table ───────────────────────────────────────────────

function ActionItemsPanel({ items }) {
  const [sortBy, setSortBy] = useState("priority");
  const [filterPriority, setFilterPriority] = useState("all");

  const sorted = useMemo(() => {
    if (!items?.length) return [];
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    let filtered = filterPriority === "all" ? items : items.filter(i => i.priority === filterPriority);
    return [...filtered].sort((a, b) => {
      if (sortBy === "priority") return (priorityOrder[a.priority] || 9) - (priorityOrder[b.priority] || 9);
      if (sortBy === "timeline") return (a.timeline || "").localeCompare(b.timeline || "");
      return 0;
    });
  }, [items, sortBy, filterPriority]);

  if (!items?.length) return null;

  return (
    <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="action-items-panel">
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
        <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] flex items-center gap-2">
          <Target className="w-3.5 h-3.5" /> Action Items ({items.length})
        </CardTitle>
        <div className="flex items-center gap-2">
          <select
            value={filterPriority}
            onChange={e => setFilterPriority(e.target.value)}
            className="text-[10px] bg-[var(--qn-surface)] border border-[var(--qn-border)] rounded px-2 py-0.5 text-[var(--qn-foreground)]"
            data-testid="action-filter-priority"
          >
            <option value="all">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="text-[10px] bg-[var(--qn-surface)] border border-[var(--qn-border)] rounded px-2 py-0.5 text-[var(--qn-foreground)]"
            data-testid="action-sort-by"
          >
            <option value="priority">Sort: Priority</option>
            <option value="timeline">Sort: Timeline</option>
          </select>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="action-items-table">
            <thead>
              <tr className="border-b border-[var(--qn-border)]">
                <th className="px-3 py-2 text-left text-[var(--qn-foreground-muted)] font-medium">Priority</th>
                <th className="px-3 py-2 text-left text-[var(--qn-foreground-muted)] font-medium">Action</th>
                <th className="px-3 py-2 text-left text-[var(--qn-foreground-muted)] font-medium">Owner</th>
                <th className="px-3 py-2 text-left text-[var(--qn-foreground-muted)] font-medium">Timeline</th>
                <th className="px-3 py-2 text-left text-[var(--qn-foreground-muted)] font-medium">Impact</th>
                <th className="px-3 py-2 text-left text-[var(--qn-foreground-muted)] font-medium">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((item, i) => {
                const pc = PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.medium;
                return (
                  <tr key={item.id || i} className="border-b border-[var(--qn-border)]/50 hover:bg-[var(--qn-surface)]/30" data-testid={`action-item-${item.id || i}`}>
                    <td className="px-3 py-2">
                      <Badge className={`${pc.bg} ${pc.text} ${pc.border} border text-[10px]`}>{item.priority}</Badge>
                    </td>
                    <td className="px-3 py-2 max-w-[300px]">
                      <div className="font-medium text-[var(--qn-foreground)] text-xs">{item.title}</div>
                      {item.description && <div className="text-[var(--qn-foreground-muted)] text-[10px] mt-0.5 line-clamp-2">{item.description}</div>}
                    </td>
                    <td className="px-3 py-2 text-[var(--qn-foreground)]/80">{item.owner || "—"}</td>
                    <td className="px-3 py-2 text-[var(--qn-foreground)]/80">{item.timeline || "—"}</td>
                    <td className="px-3 py-2 text-[var(--qn-foreground)]/80 max-w-[150px]">{item.expectedImpact || "—"}</td>
                    <td className="px-3 py-2">
                      {item.confidence != null && (
                        <div className="flex items-center gap-1">
                          <div className="w-12 h-1.5 rounded-full bg-[var(--qn-surface)]">
                            <div className="h-full rounded-full bg-[var(--qn-primary)]" style={{ width: `${(item.confidence * 100)}%` }} />
                          </div>
                          <span className="text-[10px] text-[var(--qn-foreground-muted)]">{(item.confidence * 100).toFixed(0)}%</span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


// ── Pipeline Progress Indicator ──────────────────────────────────────

function PipelineProgress({ opResults }) {
  const phases = [
    { key: "synthesis", label: "Synthesizing", ids: ["OP_001", "OP_002", "OP_003", "OP_004"] },
    { key: "writing", label: "Writing", ids: ["OP_005", "OP_006", "OP_007"] },
    { key: "designing", label: "Designing", ids: ["OP_008", "OP_009", "OP_010"] },
    { key: "assembling", label: "Assembling", ids: ["OP_011", "OP_012"] },
  ];

  return (
    <div className="flex items-center gap-1" data-testid="pipeline-progress">
      {phases.map((phase, i) => {
        const done = phase.ids.every(id => opResults[id]);
        const active = phase.ids.some(id => opResults[id]) && !done;
        return (
          <div key={phase.key} className="flex items-center gap-1">
            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
              done ? "bg-emerald-500/15 text-emerald-400" : active ? "bg-amber-500/15 text-amber-400 animate-pulse" : "bg-[var(--qn-surface)] text-[var(--qn-foreground-muted)]"
            }`}>
              {done ? <CheckCircle className="w-3 h-3" /> : active ? <Zap className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
              {phase.label}
            </div>
            {i < phases.length - 1 && <ChevronRight className="w-3 h-3 text-[var(--qn-foreground-muted)]" />}
          </div>
        );
      })}
    </div>
  );
}


// ── Savings Summary ──────────────────────────────────────────────────

function SavingsBar({ opResults }) {
  const opEntries = Object.values(opResults);
  if (!opEntries.length) return null;
  const totalOriginal = opEntries.reduce((s, r) => s + (r.original_cost_estimate || 0), 0);
  const totalOptimized = opEntries.reduce((s, r) => s + (r.optimized_cost_estimate || 0), 0);
  const totalSavings = totalOriginal - totalOptimized;
  const avgSavings = opEntries.reduce((s, r) => s + (r.savings_percent || 0), 0) / opEntries.length;

  if (avgSavings <= 0) return null;

  return (
    <div className="flex items-center gap-3 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid="savings-bar">
      <TrendingUp className="w-4 h-4 text-emerald-400" />
      <span className="text-xs text-emerald-400 font-medium">
        Proxy Optimized: {avgSavings.toFixed(1)}% avg savings across {opEntries.length} pipeline agents
      </span>
      {totalSavings > 0 && (
        <Badge className="bg-emerald-500/20 text-emerald-300 text-[10px]">
          Est. saved ${totalSavings.toFixed(4)}
        </Badge>
      )}
    </div>
  );
}


// ── Cultural Compliance Panel (kultur.dev) ──────────────────────────

function KulturCompliancePanel({ kulturScreening }) {
  const [expanded, setExpanded] = useState(false);
  if (!kulturScreening || Object.keys(kulturScreening).length === 0) return null;

  const entries = Object.entries(kulturScreening);
  const avgScore = entries.reduce((s, [, v]) => s + (v.compliance_score || 0), 0) / entries.length;
  const allIssues = entries.flatMap(([agentId, v]) => (v.issues || []).map(i => ({ ...i, agentId })));
  const allRecs = entries.flatMap(([, v]) => v.recommendations || []);
  const isMock = entries.some(([, v]) => v.is_mock);

  const scoreColor = avgScore >= 85 ? "text-emerald-400" : avgScore >= 70 ? "text-amber-400" : "text-red-400";
  const scoreBg = avgScore >= 85 ? "bg-emerald-500/10 border-emerald-500/20" : avgScore >= 70 ? "bg-amber-500/10 border-amber-500/20" : "bg-red-500/10 border-red-500/20";

  return (
    <Card className={`bg-[var(--qn-card)] border-[var(--qn-border)] ${scoreBg}`} data-testid="kultur-compliance-panel">
      <CardHeader className="py-3 px-4 cursor-pointer flex flex-row items-center justify-between" onClick={() => setExpanded(!expanded)}>
        <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] flex items-center gap-2">
          <Shield className="w-3.5 h-3.5" /> Cultural Compliance — kultur.dev
          {isMock && <Badge className="bg-[var(--qn-surface)] text-[var(--qn-foreground-muted)] text-[8px]">MOCK</Badge>}
        </CardTitle>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-bold ${scoreColor}`}>{avgScore.toFixed(0)}%</span>
          <Badge className={`${scoreBg} ${scoreColor} text-[10px] border`}>
            {avgScore >= 85 ? "PASS" : avgScore >= 70 ? "REVIEW" : "FLAG"}
          </Badge>
          {expanded ? <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" />}
        </div>
      </CardHeader>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
            <CardContent className="px-4 pb-4 pt-0 space-y-3">
              {/* Per-agent scores */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {entries.map(([agentId, v]) => (
                  <div key={agentId} className="p-2 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]/50">
                    <div className="text-[10px] text-[var(--qn-foreground-muted)]">{agentId.replace("OP_", "OP-")}</div>
                    <div className={`text-sm font-bold ${v.compliance_score >= 85 ? "text-emerald-400" : v.compliance_score >= 70 ? "text-amber-400" : "text-red-400"}`}>
                      {v.compliance_score}%
                    </div>
                    <div className="text-[9px] text-[var(--qn-foreground-muted)]">{v.context?.replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
              {/* Issues found */}
              {allIssues.length > 0 && (
                <div>
                  <h4 className="text-[10px] font-semibold uppercase text-[var(--qn-foreground-muted)] mb-1">Issues Detected ({allIssues.length})</h4>
                  {allIssues.map((issue, i) => (
                    <div key={i} className="p-2 rounded bg-[var(--qn-surface)]/20 border border-[var(--qn-border)]/30 mb-1.5">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Badge className={`text-[8px] ${issue.severity === "high" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>{issue.severity}</Badge>
                        <span className="text-[10px] font-medium text-[var(--qn-foreground)]">{issue.category}</span>
                      </div>
                      <p className="text-[10px] text-[var(--qn-foreground)]/80">{issue.description}</p>
                      {issue.suggestion && <p className="text-[10px] text-emerald-400/80 mt-0.5">Fix: {issue.suggestion}</p>}
                    </div>
                  ))}
                </div>
              )}
              {/* Regional recommendations */}
              {allRecs.length > 0 && (
                <div>
                  <h4 className="text-[10px] font-semibold uppercase text-[var(--qn-foreground-muted)] mb-1">Regional Considerations</h4>
                  {allRecs.map((rec, i) => (
                    <div key={i} className="flex items-start gap-2 text-[10px] text-[var(--qn-foreground)]/80 mb-1">
                      <Badge className="bg-[var(--qn-surface)] text-[var(--qn-foreground-muted)] text-[8px] shrink-0">{rec.region}</Badge>
                      <span>{rec.recommendation}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}


// ── Main Component ───────────────────────────────────────────────────

export function hasOutputPipeline(executionDetail) {
  if (!executionDetail) return false;
  const results = executionDetail.agent_results || {};
  return Object.keys(results).some(id => id.startsWith("OP_"));
}

export default function UniversalResultsViewer({ executionDetail }) {
  const [expandedSections, setExpandedSections] = useState({ exec: true, actions: true, charts: true, detail: false, raw: false });
  const [fullReportOpen, setFullReportOpen] = useState(false);

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Parse Output Pipeline results
  const { opResults, analystResults, execSummary, detailAnalysis, recommendations, actionItems, chartSpecs, presentationDoc, totalSavings } = useMemo(() => {
    const results = executionDetail?.agent_results || {};
    const op = {}, analysts = {};

    for (const [id, r] of Object.entries(results)) {
      if (id.startsWith("OP_")) op[id] = r;
      else analysts[id] = r;
    }

    const execSummary = op.OP_005?.output || "";
    const detailAnalysis = op.OP_006?.output || "";
    const recommendations = op.OP_007?.output || "";
    const presentationDoc = op.OP_011?.output || "";

    const chartSpecs = safeParseJSON(op.OP_008?.output) || [];
    const rawActions = safeParseJSON(op.OP_012?.output) || [];
    const actionItems = Array.isArray(rawActions) ? rawActions : [];

    const opEntries = Object.values(op);
    const totalSavings = opEntries.reduce((s, r) => s + ((r.original_cost_estimate || 0) - (r.optimized_cost_estimate || 0)), 0);

    return { opResults: op, analystResults: analysts, execSummary, detailAnalysis, recommendations, actionItems, chartSpecs, presentationDoc, totalSavings };
  }, [executionDetail]);

  const analystCount = Object.keys(analystResults).length;
  const opCount = Object.keys(opResults).length;
  const pipelineComplete = opResults.OP_011 != null;
  const kulturScreening = executionDetail?.kultur_screening || {};

  return (
    <div className="space-y-4" data-testid="universal-results-viewer">
      {/* ── 1. Header Bar ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3" data-testid="results-header">
        <div>
          <h2 className="text-lg font-bold text-[var(--qn-foreground)]">
            {executionDetail?.swarm_name || "Execution Results"}
          </h2>
          <div className="flex items-center gap-3 mt-1 text-[10px] text-[var(--qn-foreground-muted)]">
            <span>{analystCount} analysts + {opCount} pipeline agents</span>
            <span>{fmtDuration(executionDetail?.duration_ms)}</span>
            <span>{(executionDetail?.total_tokens || 0).toLocaleString()} tokens</span>
            <span>{fmtCost(executionDetail?.total_cost)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PipelineProgress opResults={opResults} />
          {pipelineComplete && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5" onClick={() => setFullReportOpen(true)} data-testid="download-presentation-btn">
              <Download className="w-3.5 h-3.5" /> View Full Report
            </Button>
          )}
        </div>
      </div>

      {/* Savings Bar */}
      <SavingsBar opResults={opResults} />

      {/* kultur.dev Cultural Compliance */}
      <KulturCompliancePanel kulturScreening={kulturScreening} />

      {/* ── 2. Executive Briefing ──────────────────────────────────── */}
      {execSummary && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] border-l-4 border-l-[var(--qn-primary)]" data-testid="executive-briefing">
          <CardHeader className="py-3 px-4 cursor-pointer flex flex-row items-center justify-between" onClick={() => toggleSection("exec")}>
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" /> Executive Briefing
            </CardTitle>
            {expandedSections.exec ? <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" />}
          </CardHeader>
          <AnimatePresence>
            {expandedSections.exec && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                <CardContent className="px-4 pb-4 pt-0">
                  <ExecMarkdown content={execSummary} />
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      )}

      {/* ── 3. Action Items ────────────────────────────────────────── */}
      {actionItems.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2 cursor-pointer" onClick={() => toggleSection("actions")}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] flex items-center gap-2">
              <Target className="w-3.5 h-3.5" /> Prioritized Action Items ({actionItems.length})
            </h3>
            {expandedSections.actions ? <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" />}
          </div>
          <AnimatePresence>
            {expandedSections.actions && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}><ActionItemsPanel items={actionItems} /></motion.div>}
          </AnimatePresence>
        </div>
      )}

      {/* ── 4. Visualizations Gallery ──────────────────────────────── */}
      {chartSpecs.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2 cursor-pointer" onClick={() => toggleSection("charts")}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] flex items-center gap-2">
              <BarChart3 className="w-3.5 h-3.5" /> Data Visualizations ({chartSpecs.length})
            </h3>
            {expandedSections.charts ? <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" />}
          </div>
          <AnimatePresence>
            {expandedSections.charts && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {chartSpecs.map((spec, i) => <ChartRenderer key={i} spec={spec} />)}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* ── 5. Recommendations ─────────────────────────────────────── */}
      {recommendations && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="recommendations-panel">
          <CardHeader className="py-3 px-4 cursor-pointer flex flex-row items-center justify-between" onClick={() => toggleSection("recs")}>
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] flex items-center gap-2">
              <ArrowUpRight className="w-3.5 h-3.5" /> Recommendations
            </CardTitle>
            {expandedSections.recs ? <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" />}
          </CardHeader>
          <AnimatePresence>
            {expandedSections.recs && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                <CardContent className="px-4 pb-4 pt-0">
                  <ExecMarkdown content={recommendations} />
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      )}

      {/* ── 6. Detailed Analysis Accordion ─────────────────────────── */}
      {detailAnalysis && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="detailed-analysis-panel">
          <CardHeader className="py-3 px-4 cursor-pointer flex flex-row items-center justify-between" onClick={() => toggleSection("detail")}>
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] flex items-center gap-2">
              <Layers className="w-3.5 h-3.5" /> Detailed Analysis
            </CardTitle>
            {expandedSections.detail ? <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" />}
          </CardHeader>
          <AnimatePresence>
            {expandedSections.detail && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                <CardContent className="px-4 pb-4 pt-0 overflow-y-auto" style={{ maxHeight: "none" }}>
                  <ExecMarkdown content={detailAnalysis} />
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      )}

      {/* ── 7. Raw Agent Outputs ────────────────────────────────────── */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="raw-agent-outputs">
        <CardHeader className="py-3 px-4 cursor-pointer flex flex-row items-center justify-between" onClick={() => toggleSection("raw")}>
          <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] flex items-center gap-2">
            <Brain className="w-3.5 h-3.5" /> Raw Agent Outputs ({analystCount})
          </CardTitle>
          {expandedSections.raw ? <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" />}
        </CardHeader>
        <AnimatePresence>
          {expandedSections.raw && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              <CardContent className="px-3 pb-3 pt-0 space-y-2 max-h-[500px] overflow-y-auto">
                {Object.values(analystResults).sort((a, b) => (a.label || "").localeCompare(b.label || "")).map((r) => (
                  <RawAgentCard key={r.node_id} result={r} />
                ))}
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>

      {/* ── 8. Pipeline Agent Details ──────────────────────────────── */}
      {opCount > 0 && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="pipeline-agent-details">
          <CardHeader className="py-3 px-4 cursor-pointer flex flex-row items-center justify-between" onClick={() => toggleSection("pipeline")}>
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-amber-400" /> Output Pipeline Agents ({opCount}) — Model Routing
            </CardTitle>
            {expandedSections.pipeline ? <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" />}
          </CardHeader>
          <AnimatePresence>
            {expandedSections.pipeline && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                <CardContent className="px-3 pb-3 pt-0 space-y-2 max-h-[400px] overflow-y-auto">
                  {Object.entries(opResults).sort(([a], [b]) => a.localeCompare(b)).map(([id, r]) => (
                    <div key={id} className="p-2 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]/50" data-testid={`pipeline-agent-${id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-amber-500/15 text-amber-400 text-[9px]">{id}</Badge>
                          <span className="text-xs font-medium text-[var(--qn-foreground)]">{r.label}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-[var(--qn-foreground-muted)]">
                          {r.preferred_model && r.routed_model && r.preferred_model !== r.routed_model && (
                            <Badge className="bg-emerald-500/15 text-emerald-400 text-[9px]">
                              {r.preferred_model} &rarr; {r.routed_model} ({r.savings_percent?.toFixed(1)}% saved)
                            </Badge>
                          )}
                          {r.routed_model && <span className="text-[var(--qn-foreground)]/60">{r.routed_model}</span>}
                          <span>{r.latency_ms}ms</span>
                          <span>{fmtCost(r.cost)}</span>
                        </div>
                      </div>
                      {r.routing_reason && <p className="text-[10px] text-[var(--qn-foreground-muted)] italic">{r.routing_reason}</p>}
                    </div>
                  ))}
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      )}

      {/* ── Full Report Dialog ─────────────────────────────────────── */}
      <Dialog open={fullReportOpen} onOpenChange={setFullReportOpen}>
        <DialogContent className="max-w-[900px] max-h-[90vh] overflow-y-auto bg-[var(--qn-card)] border-[var(--qn-border)] print-friendly" data-testid="full-report-dialog">
          <div className="flex items-center justify-between mb-4 no-print">
            <h2 className="text-lg font-bold text-[var(--qn-foreground)]">
              {executionDetail?.swarm_name || "Full Report"}
            </h2>
            <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => window.print()} data-testid="print-report-btn">
              <Printer className="w-3.5 h-3.5" /> Print / PDF
            </Button>
          </div>
          <div className="space-y-6 report-content">
            {execSummary && (
              <section className="page-break-after">
                <h2 className="text-lg font-bold text-[var(--qn-foreground)] mb-3 border-b border-[var(--qn-border)] pb-2">Executive Summary</h2>
                <ExecMarkdown content={execSummary} />
              </section>
            )}
            {/* Chart data as tables for PDF compatibility */}
            {chartSpecs.length > 0 && (
              <section className="page-break-after">
                <h2 className="text-lg font-bold text-[var(--qn-foreground)] mb-3 border-b border-[var(--qn-border)] pb-2">Key Data &amp; Visualizations</h2>
                {chartSpecs.map((spec, i) => (
                  <div key={i} className="mb-4">
                    <h3 className="text-sm font-semibold text-[var(--qn-foreground)] mb-1">{spec.title}</h3>
                    {spec.insight && <p className="text-xs text-[var(--qn-foreground)]/70 italic mb-2">{spec.insight}</p>}
                    {spec.data && (
                      <table className="w-full text-xs border-collapse mb-2">
                        <thead>
                          <tr>
                            {Object.keys(spec.data[0] || {}).map(k => (
                              <th key={k} className="border border-[var(--qn-border)] px-2 py-1 bg-[var(--qn-surface)] text-left font-semibold text-[var(--qn-foreground)]">{k}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {spec.data.slice(0, 20).map((row, ri) => (
                            <tr key={ri}>
                              {Object.values(row).map((v, ci) => (
                                <td key={ci} className="border border-[var(--qn-border)] px-2 py-1 text-[var(--qn-foreground)]/80">{typeof v === "number" ? v.toLocaleString() : String(v)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </section>
            )}
            {recommendations && (
              <section className="page-break-after">
                <h2 className="text-lg font-bold text-[var(--qn-foreground)] mb-3 border-b border-[var(--qn-border)] pb-2">Recommendations</h2>
                <ExecMarkdown content={recommendations} />
              </section>
            )}
            {/* Action Items as table for PDF */}
            {actionItems.length > 0 && (
              <section className="page-break-after">
                <h2 className="text-lg font-bold text-[var(--qn-foreground)] mb-3 border-b border-[var(--qn-border)] pb-2">Action Items ({actionItems.length})</h2>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr>
                      <th className="border border-[var(--qn-border)] px-2 py-1 bg-[var(--qn-surface)] text-left font-semibold">Priority</th>
                      <th className="border border-[var(--qn-border)] px-2 py-1 bg-[var(--qn-surface)] text-left font-semibold">Action</th>
                      <th className="border border-[var(--qn-border)] px-2 py-1 bg-[var(--qn-surface)] text-left font-semibold">Owner</th>
                      <th className="border border-[var(--qn-border)] px-2 py-1 bg-[var(--qn-surface)] text-left font-semibold">Timeline</th>
                      <th className="border border-[var(--qn-border)] px-2 py-1 bg-[var(--qn-surface)] text-left font-semibold">Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actionItems.map((item, i) => (
                      <tr key={i}>
                        <td className="border border-[var(--qn-border)] px-2 py-1 font-semibold">{item.priority}</td>
                        <td className="border border-[var(--qn-border)] px-2 py-1">{item.title}</td>
                        <td className="border border-[var(--qn-border)] px-2 py-1">{item.owner || "—"}</td>
                        <td className="border border-[var(--qn-border)] px-2 py-1">{item.timeline || "—"}</td>
                        <td className="border border-[var(--qn-border)] px-2 py-1">{item.expectedImpact || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
            {detailAnalysis && (
              <section className="page-break-after">
                <h2 className="text-lg font-bold text-[var(--qn-foreground)] mb-3 border-b border-[var(--qn-border)] pb-2">Detailed Analysis</h2>
                <ExecMarkdown content={detailAnalysis} />
              </section>
            )}
            {presentationDoc && (
              <section>
                <h2 className="text-lg font-bold text-[var(--qn-foreground)] mb-3 border-b border-[var(--qn-border)] pb-2">Assembled Presentation</h2>
                <ExecMarkdown content={presentationDoc} />
              </section>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-friendly {
            max-width: 100% !important;
            max-height: none !important;
            overflow: visible !important;
            background: white !important;
            color: black !important;
            position: static !important;
          }
          .report-content { overflow: visible !important; }
          .report-content h2 { color: black !important; border-color: #ccc !important; }
          .report-content h3 { color: #222 !important; }
          .report-content p, .report-content li { color: #333 !important; }
          .report-content table { border-color: #ccc !important; }
          .report-content th { background: #f5f5f5 !important; color: black !important; border-color: #ccc !important; }
          .report-content td { color: #333 !important; border-color: #ccc !important; }
          .page-break-after { page-break-after: auto; }
          section { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}


// ── Raw Agent Card (sub-component) ───────────────────────────────────

function RawAgentCard({ result }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="p-2 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]/50" data-testid={`raw-agent-${result.node_id}`}>
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          <Badge className="bg-[var(--qn-primary)]/10 text-[var(--qn-primary)] text-[9px]">{result.type}</Badge>
          <span className="text-xs font-medium text-[var(--qn-foreground)]">{result.label || result.node_id}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[var(--qn-foreground-muted)]">
          <span>{result.tokens?.toLocaleString()} tok</span>
          <span>{fmtCost(result.cost)}</span>
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="mt-2">
            <div className="text-xs text-[var(--qn-foreground)]/80 bg-[var(--qn-bg)]/50 rounded p-2 max-h-[300px] overflow-y-auto">
              <ExecMarkdown content={result.output} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
